import React, { useState } from "react";
import { Link } from "react-router-dom";

interface CustomizePavelProps {
    placeholderImage?: string;
}

const GradientBorderCard: React.FC<{
    children: React.ReactNode;
    className?: string;
}> = ({ children, className = "" }) => (
    <div
        className={`relative ${className}`}
        style={{
            background:
                "linear-gradient(#0a0a0a, #0a0a0a) padding-box, linear-gradient(90deg, #00858F, #E6007E) border-box",
            border: "4px solid transparent",
        }}
    >
        {children}
    </div>
);

interface PanelDiagramProps {
    sizeLabel: string;
    deployable: 1 | 2 | 3;
    power: string;
    active: boolean;
    onClick: () => void;
}

const PanelDiagram: React.FC<PanelDiagramProps> = ({
                                                       sizeLabel, deployable, power, active, onClick,
                                                   }) => {
    const cubeW = 26; const cubeH = 42;
    const panelW = 26; const panelH = 36;
    const gap = 3;
    const totalW = cubeW + (panelW + gap) * deployable * 2 + 20;
    const cx = totalW / 2; const cy = 26;

    const wings: React.ReactNode[] = [];
    for (let i = 0; i < deployable; i++) {
        const offsetR = cubeW / 2 + gap + (panelW + gap) * i;
        const offsetL = -(cubeW / 2 + gap + (panelW + gap) * i + panelW);
        [offsetR, offsetL].forEach((offset, side) => {
            wings.push(
                <g key={`${side}${i}`}>
                    <rect x={cx + offset} y={cy - panelH / 2} width={panelW} height={panelH}
                          fill={active ? "rgba(0,133,143,0.30)" : "rgba(255,255,255,0.06)"}
                          stroke={active ? "#00C9D6" : "rgba(255,255,255,0.3)"}
                          strokeWidth={active ? "1.5" : "1"} />
                    {[1, 2, 3].map((row) => (
                        <line key={row}
                              x1={cx + offset + 3} y1={cy - panelH / 2 + (panelH / 4) * row}
                              x2={cx + offset + panelW - 3} y2={cy - panelH / 2 + (panelH / 4) * row}
                              stroke={active ? "rgba(0,201,214,0.5)" : "rgba(255,255,255,0.1)"}
                              strokeWidth="0.5" />
                    ))}
                </g>
            );
        });
    }

    return (
        <button onClick={onClick}
                className="flex flex-col items-center gap-1.5 focus:outline-none transition-all duration-300"
                style={{ opacity: active ? 1 : 0.55 }}>
            <svg viewBox={`0 0 ${totalW} 58`} width="100%" height="52"
                 xmlns="http://www.w3.org/2000/svg" style={{ overflow: "visible" }}>
                {wings}
                <rect x={cx - cubeW / 2} y={cy - cubeH / 2} width={cubeW} height={cubeH}
                      fill={active ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.04)"}
                      stroke={active ? "white" : "rgba(255,255,255,0.35)"}
                      strokeWidth={active ? "1.5" : "1"} />
                <text x={cx} y={cy + 4} textAnchor="middle"
                      fontSize="6" fill={active ? "white" : "rgba(255,255,255,0.4)"}
                      fontWeight="bold" fontFamily="monospace">{sizeLabel}</text>
            </svg>
            <span className="text-[12px] uppercase tracking-widest font-bold"
                  style={{ color: active ? "#00C9D6" : "rgba(255,255,255,0.4)" }}>
                {deployable === 1 ? "SINGLE" : deployable === 2 ? "DOUBLE" : "TRIPLE"}
            </span>
            {/* wattage intentionally hidden */}
            <span className="text-xl font-bold tracking-tight leading-none transition-all duration-300"
                  style={{ color: active ? "white" : "rgba(255,255,255,0.35)" }}>
            </span>
        </button>
    );
};

const CustomizePavel: React.FC<CustomizePavelProps> = ({
                                                           placeholderImage = "/SARA-D.png",
                                                       }) => {
    const [activeSize, setActiveSize] = useState<"6u" | "8u">("6u");
    const [activeDeploy, setActiveDeploy] = useState<1 | 2 | 3>(1);

    const powerMap: Record<string, Record<number, string>> = {
        "6u": { 1: "19W", 2: "38W", 3: "57W" },
        "8u": { 1: "24W", 2: "48W", 3: "72W" },
    };

    const sizeLabel     = activeSize === "6u" ? "6U/12U" : "8U/16U";
    const selectedPower = powerMap[activeSize][activeDeploy];
    const deployLabel   = activeDeploy === 1 ? "SINGLE" : activeDeploy === 2 ? "DOUBLE" : "TRIPLE";

    const customOptions = [
        { label: "SOLAR CELL ARRANGEMENT",   sub: "Various triple junction types, layout optimized per mission" },
        { label: "ELECTRICAL LAYOUT",         sub: "Series / parallel string configuration" },
        { label: "CONNECTOR TYPE AND PINOUT", sub: "Adapted to your harness and interface" },
        { label: "MECHANICAL INTERFACES",     sub: "Hinges on long or short sides, matched to platform" },
        { label: "HDRM TYPE",                 sub: "Shockless/resettable hold-down with flexible positioning" },
    ];

    return (
        <div className="w-full max-w-[1600px] mx-auto text-white">

            <h2 data-aos="fade-zoom-in" data-aos-offset="100px" data-aos-easing="ease-in-sine" data-aos-duration="600"
                className="my-12 text-3xl md:text-5xl leading-120 tracking-tighter text-center uppercase font-bold text-white">
                CUSTOMIZE PAVEL
            </h2>

            <div className="w-full mb-8 overflow-hidden"
                 data-aos="fade-zoom-in" data-aos-offset="100px" data-aos-delay="100"
                 data-aos-easing="ease-in-sine" data-aos-duration="600">
                <img src={placeholderImage} alt="PAVEL customization"
                     className="w-full h-[16rem] md:h-[24rem] object-cover opacity-80" />
            </div>

            {/* ── Row 1: DIMENSIONS + CUSTOMIZATION OPTIONS ── */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-5 px-4 md:px-10 mb-5"
                 data-aos="fade-zoom-in" data-aos-offset="100px" data-aos-delay="200"
                 data-aos-easing="ease-in-sine" data-aos-duration="600">

                {/* ── DIMENSIONS ── */}
                <GradientBorderCard className="md:col-span-3 p-7 flex flex-col gap-6">
                    <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 pb-3"
                        style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
                        DIMENSIONS
                    </h3>

                    {/* Size toggle */}
                    <div className="flex gap-3">
                        {(["6u", "8u"] as const).map((s) => (
                            <button key={s} onClick={() => setActiveSize(s)}
                                    className="flex-1 py-3 text-xs font-bold uppercase tracking-widest transition-all duration-200 flex items-center justify-center"
                                    style={{
                                        borderRadius: "999px",
                                        ...(activeSize === s
                                            ? { background: "linear-gradient(90deg,#00858F,#E6007E)", border: "none", color: "white" }
                                            : { background: "transparent", border: "2px solid rgba(255,255,255,0.25)", color: "rgba(255,255,255,0.5)" }),
                                    }}>
                                {s === "6u" ? "6U / 12U" : "8U / 16U"}
                            </button>
                        ))}
                    </div>

                    {/* Panel diagrams */}
                    <div className="grid grid-cols-3 gap-3 ">
                        {([1, 2, 3] as const).map((d) => (
                            <div key={d} style={{
                                borderRadius: "16px", padding: "12px 8px",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                ...(activeDeploy === d
                                    ? { background: "linear-gradient(#111,#111) padding-box, linear-gradient(90deg,#00858F,#E6007E) border-box", border: "2px solid transparent" }
                                    : { background: "transparent", border: "2px solid rgba(255,255,255,0.12)" }),
                            }}>
                                <PanelDiagram sizeLabel={sizeLabel} deployable={d}
                                              power={powerMap[activeSize][d]}
                                              active={activeDeploy === d}
                                              onClick={() => setActiveDeploy(d)} />
                            </div>
                        ))}
                    </div>

                    <p className="text-[9px] text-white/30 uppercase tracking-wide leading-relaxed">
                        Based on Azur Space 3G30A. Other cell types available on request.
                    </p>

                    {/* ── Power + CTA ── */}
                    <div
                        className="flex flex-col sm:flex-row items-stretch"
                        style={{
                            background: "linear-gradient(#0a0a0a, #0a0a0a) padding-box, linear-gradient(90deg, #00858F, #E6007E) border-box",
                            border: "2px solid transparent",
                        }}>
                        {/* Power readout */}
                        <div className="flex items-center gap-3 px-5 py-4 flex-1">
                            <span className="text-5xl font-bold leading-none tracking-tight flex-shrink-0"
                                  style={{
                                      background: "linear-gradient(90deg,#00858F,#E6007E)",
                                      WebkitBackgroundClip: "text",
                                      WebkitTextFillColor: "transparent",
                                  }}>
                                {selectedPower}
                            </span>
                            <div className="flex flex-col">
                                <span className="text-white font-bold uppercase tracking-widest text-xs leading-tight">
                                    {sizeLabel} · {deployLabel}
                                </span>
                                <span className="text-white/40 text-[9px] uppercase tracking-wide mt-0.5">
                                    BOL POWER PER WING
                                </span>
                            </div>
                        </div>
                        {/* CTA */}
                        <div className="flex items-center justify-start sm:justify-center px-5 pb-5 pt-0 sm:py-4 flex-shrink-0">
                            <Link to="/contact" className="w-full sm:w-auto">
                                <button className="w-full sm:w-auto px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-transform duration-200 hover:scale-105"
                                        style={{ background: "linear-gradient(90deg,#00858F,#E6007E)", borderRadius: "999px" }}>
                                    DISCUSS THIS CONFIGURATION
                                </button>
                            </Link>
                        </div>
                    </div>
                </GradientBorderCard>

                {/* ── CUSTOMIZATION OPTIONS ── */}
                <GradientBorderCard className="md:col-span-2 p-7 flex flex-col gap-5">
                    <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-white/50 pb-3"
                        style={{ borderBottom: "1px solid rgba(255,255,255,0.15)" }}>
                        CUSTOMIZATION OPTIONS
                    </h3>
                    <ul className="flex flex-col flex-1 justify-around">
                        {customOptions.map((item, i, arr) => (
                            <li key={item.label}>
                                <div className="flex items-start gap-4 py-3">
                                    <span className="flex-shrink-0 w-2 h-2 mt-1.5"
                                          style={{ background: "linear-gradient(135deg,#00858F,#E6007E)" }} />
                                    <div>
                                        <p className="text-sm uppercase tracking-wide text-white font-bold leading-tight">
                                            {item.label}
                                        </p>
                                        <p className="text-xs text-white/45 mt-1 normal-case">{item.sub}</p>
                                    </div>
                                </div>
                                {i < arr.length - 1 && (
                                    <div className="h-px ml-6" style={{ background: "rgba(255,255,255,0.1)" }} />
                                )}
                            </li>
                        ))}
                    </ul>
                </GradientBorderCard>
            </div>

            {/* ── Row 2: COMPATIBLE WITH ── */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 px-4 md:px-10">

                {[
                    {
                        to: "/products/holly",
                        img: "/products/holly%20%201299x602.png",
                        fallback: "/SARA-D.png",
                        title: "COMPATIBLE WITH HOLLY",
                        sub: "90° AND 180° VERSION",
                        delay: "400",
                    },
                    {
                        to: "/products/sara",
                        img: "/products/sara%201299x602.png",
                        fallback: "/SARA-D.png",
                        title: "COMPATIBLE WITH SARA",
                        sub: "Solar Array drive Assembly",
                        delay: "500",
                    },
                ].map((item) => (
                    <Link key={item.to} to={item.to} className="block group">
                        <div
                            data-aos="fade-zoom-in"
                            data-aos-offset="100px"
                            data-aos-delay={item.delay}
                            data-aos-easing="ease-in-sine"
                            data-aos-duration="600"
                            className="p-6 flex flex-row items-center justify-between gap-4 transition-all duration-300 hover:scale-[1.02]"
                            style={{ background: "linear-gradient(90deg, #00858F, #E6007E)" }}
                        >
                            {/* Left: text + CTA */}
                            <div className="flex flex-col gap-3 flex-1">

                                <h3 className="text-md font-bold uppercase tracking-widest text-white/90">
                                    <span className="inline-block w-2 h-2 mb-0.5 mr-2 flex-shrink-0 bg-white" />
                                    {item.title}
                                </h3>
                                <p className="text-sm uppercase text-white/70">{item.sub}</p>
                                {/* Discover more */}
                                <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs font-bold uppercase tracking-widest text-white">
                            DISCOVER MORE
                        </span>
                                    <img
                                        src="/commons/arrow_right.svg"
                                        alt=""
                                        className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1.5"
                                    />
                                </div>
                            </div>

                            {/* Right: image */}
                            <div className="flex items-center justify-end w-[38%] sm:w-auto">
                                <img
                                    src={item.img}
                                    alt={item.title}
                                    className="h-32 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
                                    onError={(e) => { (e.target as HTMLImageElement).src = item.fallback; }}
                                />
                            </div>
                        </div>
                    </Link>
                ))}

            </div>
        </div>
    );
};

export default CustomizePavel;
